import { FeatureCollection } from "geojson";

import { groupFilesByBasename } from "./fileBundles";
import { describeGdalFailure, GdalLoadError } from "./gdalErrors";
import type { Gdal } from "./gdalRuntime";
import { loadGdal } from "./loadGdal";

/** The step a conversion is currently on. */
export type ConversionStage = "loading-converter" | "converting";

export interface ConvertFilesOptions {
  onStageChange?: (stage: ConversionStage) => void;
}

/** Describes a dataset GDAL opened, for diagnosing a failed conversion. */
export interface GdalDatasetSummary {
  driver?: string;
  layers?: string;
  featureCount?: number;
  crs?: string;
}

/** Thrown when GDAL cannot read or convert the given files. */
export class GdalConversionError extends Error {
  constructor(
    public readonly detail: string,
    public readonly context: { files: string[]; stage: "open" | "convert"; datasets?: GdalDatasetSummary[] }
  ) {
    super(`GDAL failed to ${context.stage} ${context.files.join(", ")}: ${detail}`);
    this.name = "GdalConversionError";
  }
}

type Dataset = Awaited<ReturnType<Gdal["open"]>>["datasets"][number];

/** The subset of the ogrinfo JSON report that gdal3.js attaches to each opened dataset. */
interface OgrInfo {
  driverShortName?: string;
  layers?: {
    name: string;
    featureCount?: number;
    geometryFields?: { coordinateSystem?: { projjson?: { id?: { authority: string; code: number | string } }; wkt?: string } }[];
  }[];
}

const describeCrs = (layers: NonNullable<OgrInfo["layers"]>): string => {
  const crs = layers[0]?.geometryFields?.[0]?.coordinateSystem;
  if (crs?.projjson?.id) return `${crs.projjson.id.authority}:${crs.projjson.id.code}`;
  return crs?.wkt?.slice(0, 120) ?? "(none)";
};

const summarise = ({ info }: Dataset): GdalDatasetSummary => {
  if (!info) return {};

  const { driverShortName, layers = [] } = info as OgrInfo;
  return {
    driver: driverShortName,
    layers: layers.map((layer) => layer.name).join(", "),
    featureCount: layers.reduce((total, layer) => total + (layer.featureCount ?? 0), 0),
    crs: describeCrs(layers),
  };
};

/** Converts the files of one dataset into GeoJSON, one collection per dataset GDAL opens. */
const convertBundle = async (gdal: Gdal, files: File[]): Promise<FeatureCollection[]> => {
  const names = files.map((file) => file.name);

  let datasets: Dataset[];
  let errors: string[] = [];
  try {
    // gdal3.js mounts an array of File objects, but only declares FileList in its types.
    ({ datasets, errors } = await gdal.open(files as unknown as FileList));
  } catch (reason) {
    throw new GdalConversionError(describeGdalFailure(reason), { files: names, stage: "open" });
  }

  if (datasets.length === 0) {
    throw new GdalConversionError(describeGdalFailure(errors), { files: names, stage: "open" });
  }

  try {
    return await Promise.all(
      datasets.map(async (dataset, index) => {
        const output = await gdal.ogr2ogr(dataset, ["-f", "GeoJSON", "-t_srs", "EPSG:4326"], `converted-${index}`);
        const bytes = await gdal.getFileBytes(output);
        return JSON.parse(new TextDecoder().decode(bytes)) as FeatureCollection;
      })
    );
  } catch (reason) {
    throw new GdalConversionError(describeGdalFailure(reason), { files: names, stage: "convert", datasets: datasets.map(summarise) });
  } finally {
    await Promise.all(datasets.map((dataset) => gdal.close(dataset)));
  }
};

/**
 * Converts dropped GIS files into GeoJSON, one collection per dataset.
 *
 * Files belonging to a multi-file format are recognised by their shared basename,
 * so a shapefile or MapInfo bundle yields a single collection.
 */
export const convertFilesToGeoJson = async (files: File[], { onStageChange }: ConvertFilesOptions = {}): Promise<FeatureCollection[]> => {
  onStageChange?.("loading-converter");
  const gdal = await loadGdal().catch((reason) => {
    throw new GdalLoadError(reason);
  });

  onStageChange?.("converting");

  // load each bundle separately so gdal3.js doesn't get confused
  const collections: FeatureCollection[] = [];
  for (const bundle of Object.values(groupFilesByBasename(files))) {
    collections.push(...(await convertBundle(gdal, bundle)));
  }
  return collections;
};
